import React, { useRef, useState, useContext, useEffect } from 'react'
import { Col, Form, Row } from 'react-angle-dashboard-components'
import { Controller, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import Select from 'react-select'
import { providerService } from '../../services'
import { menuListHoc, ValueContainer } from './custom-select'
import { commonSpecialties, tabs } from './data'
import { LocationIcon } from './icons'
import { useFindCareSearchInput } from './hooks/input'
import { useFindCareLocation } from './hooks/locations'
import { formatOptionLabel, mapSearchList } from './mappers'
import {
  getSelectStyles,
  LocationContainer,
  CareContainer,
  SeachContainer,
  SearchWrapper,
  StyledContainer,
  StyledBackground,
  StyledH1,
  Suggestion,
  SuggestionsWrapper
} from './style'
import { NavbarContext } from '../../components/Navbar/NavbarContext'
import { Tabs } from './Tabs'
import { VirtualCareCard } from './VirtualCareCard'

const FindCare: React.FC = () => {
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [isZipFocused, setIsZipFocused] = useState(false)
  const { setBackInfo } = useContext(NavbarContext)
  const searchWrapperRef = useRef<HTMLDivElement>(null)

  const { formState, control, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      zip: '84111'
    }
  })

  const zip = watch('zip')

  const {
    enableProvidersFetch,
    handleInputChange,
    handleSelection,
    handleSpecialtyClick,
    inputText,
    onSelectTab,
    selectedTab,
    selectInputRef,
    placeholder
  } = useFindCareSearchInput()

  const {
    currentLocation,
    isCoordsLoading,
    handleRedirect,
    defaultCenter,
    onMore
  } = useFindCareLocation(selectedTab, inputText, zip)

  useEffect(() => {
    setBackInfo({
      link: 'https://anglehealth.com/network-directories',
      text: 'Back to Angle'
    })
  }, [setBackInfo])

  const onSpecialtyClick = handleSpecialtyClick(handleRedirect)
  const onSelection = handleSelection(handleRedirect)

  // Search button removed according to new product requirements. Keeping it, and the related code, for now in case they change their mind

  // const onSubmit = () => {
  //   let route = routes.providersDirectory

  //   let queryParams: DirectoryQueryParam[] = [
  //     { key: 'query', value: inputText }
  //   ]
  //   if (selectedTab === CareTabs.ALL) {
  //     route = routes.providersDirectory

  //     let servicesMatchingInputs = []
  //     if (services) {
  //       const servicesList = Object.keys(services)

  //       for (const service of servicesList) {
  //         if (service.toLowerCase().includes(inputText.toLowerCase())) {
  //           servicesMatchingInputs.push(service)
  //         }
  //       }
  //     }
  //     if (servicesMatchingInputs.length) {
  //       // redirect to facilities map if input matches one of the facility services
  //       route = routes.facilitiesDirectory
  //       queryParams = [
  //         { key: 'service', value: servicesMatchingInputs.join(',') }
  //       ]
  //     } else {
  //       // Defaults to providers if the input does not match a facility service
  //       route = routes.providersDirectory
  //     }
  //   } else {
  //     // When having the Facilities or Providers tabs selected we just need to verify which of the two is selected
  //     route =
  //       selectedTab === CareTabs.FACILITY
  //         ? routes.facilitiesDirectory
  //         : routes.providersDirectory
  //   }

  //   reset()
  //   onClearSelect()
  //   handleRedirect(route, queryParams)
  // }

  const { data: providersData, isLoading: isProvidersLoading } = useQuery(
    ['searchProviders', inputText, selectedTab, currentLocation],
    () =>
      providerService.search(
        selectedTab,
        currentLocation?.lat || defaultCenter.lat,
        currentLocation?.lng || defaultCenter.lng,
        inputText
      ),
    {
      enabled: currentLocation !== null && enableProvidersFetch
    }
  )

  // const { data: services } = useQuery(['facilitySpecialties'], () =>
  //   facilityService.getFacilityTypesCheckboxFilter()
  // )

  const selectOptions = mapSearchList(providersData)

  const styles = getSelectStyles(searchWrapperRef)

  const customComponents = {
    IndicatorSeparator: () => null,
    DropdownIndicator: () => null,
    ValueContainer,
    MenuList: menuListHoc({ selectedTab, selectOptions, onMore })
  }

  return (
    <>
      <StyledBackground>
        <img
          src="/virtual-care-background.png"
          alt="virtual care background"
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%'
          }}
        />
      </StyledBackground>

      <StyledContainer>
        <CareContainer>
          <StyledH1 className="mt-3 mt-md-0">Find Care</StyledH1>
          <p className="text-muted mb-5">
            Your In-network care all in one place.
          </p>
          <Tabs className="mt-5 mb-2" tabs={tabs} onSelect={onSelectTab} />
          <SearchWrapper ref={searchWrapperRef}>
            <Row>
              <Col>
                <Form.Group
                  className="d-flex align-items-center mb-0"
                  style={{ borderBottom: '1px solid #e7eaf3' }}
                >
                  <SeachContainer isFocused={isSearchFocused}>
                    <Select
                      openMenuOnFocus
                      ref={selectInputRef}
                      isClearable
                      isLoading={isCoordsLoading || isProvidersLoading}
                      noOptionsMessage={() => 'No results available'}
                      placeholder={placeholder}
                      // Assign id for testing purposes
                      inputId="my-react-select-input"
                      formatOptionLabel={formatOptionLabel}
                      inputValue={inputText}
                      onInputChange={handleInputChange}
                      onChange={onSelection}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      onMenuOpen={() => setIsSearchFocused(true)}
                      onMenuClose={() => setIsSearchFocused(false)}
                      components={customComponents}
                      styles={styles}
                      options={selectOptions}
                      filterOption={() => true}
                    />
                  </SeachContainer>
                  <LocationContainer
                    hasError={Boolean(formState.errors.zip)}
                    isFocused={isZipFocused}
                  >
                    <LocationIcon className="mx-3" color="#6C757D" />
                    <Controller
                      name="zip"
                      control={control}
                      rules={{
                        required: 'This field is required',
                        validate: (value) =>
                          value.length === 5 || 'Must contain 5 characters'
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          style={{
                            border: 'none',
                            flexGrow: 3
                          }}
                          type="number"
                          className="bg-white"
                          placeholder="Enter Zip Code"
                          onFocus={() => setIsZipFocused(true)}
                          onBlur={() => setIsZipFocused(false)}
                        />
                      )}
                    />
                    {/* Search button removed according to new product requirements. Keeping it, and the related code, for now in case they change their mind */}
                    {/* <StyledButton
                  // Assign id for testing purposes
                  id="my-search-button"
                  style={{ flexGrow: 1 }}
                  disabled={
                    !formState.isValid || formState.isSubmitting || !inputText
                  }
                  onClick={onSubmit}
                >
                  Search
                </StyledButton> */}
                  </LocationContainer>
                </Form.Group>
              </Col>
            </Row>
            <Row className="p-3">
              <Col xs={12}>
                <p className="text-muted mb-1">Commonly searched specialties</p>
                <SuggestionsWrapper>
                  {commonSpecialties.map((s, i) => (
                    <Suggestion
                      key={i}
                      // Type of each specialty only for testing purposes
                      className={`text-muted noselect ${s.type}`}
                      onClick={() => onSpecialtyClick(s)}
                    >
                      {s.name}
                    </Suggestion>
                  ))}
                </SuggestionsWrapper>
              </Col>
            </Row>
          </SearchWrapper>
        </CareContainer>

        <VirtualCareCard />
      </StyledContainer>
    </>
  )
}

export default FindCare
